/**
 * Identidade, do lado da casca.
 *
 * Este e o unico arquivo do aplicativo que sabe QUAL e o fornecedor de conta.
 * Tudo o que fica acima — telas, contexto, arranque — so conhece o
 * `AuthControllerLike` de `ports.ts`: ha uma conta, ela pode estar ativa,
 * degradada ou desligada, e o botao de entrada tem um texto que vem daqui.
 * Trocar de fornecedor (regra 7 / ADR-0005) e mexer so aqui.
 *
 * Duas decisoes que valem a pena salvar:
 *
 *  1. **Nada disto esta no caminho critico.** A autenticacao arranca na fase
 *     diferida, depois do primeiro pintado. O arranque nunca espera por uma ida
 *     a rede — nem para restaurar, nem para renovar (regra 9, ADR-0002).
 *
 *  2. **Um token expirado nunca tranca a porta.** Quando o `@flos/auth` diz
 *     `degraded`, o aplicativo continua a mostrar tudo o que esta no aparelho e
 *     passa a leitura-apenas com o motivo na tela. Mandar para a tela de
 *     entrada alguem que esta sem rede seria trancá-la fora dos seus proprios
 *     dados, sem lhe dar forma de abrir.
 *
 * O token em si nunca passa por aqui para ser salvo: quem o guarda e o
 * `@flos/auth`, em memoria e IndexedDB. Nada dele vai para o `localStorage`
 * (docs/SECURITY.md).
 */
import type { FlosCore } from '@flos/core';
import type {
  AuthControllerLike,
  AuthModulePort,
  AuthSnapshot,
  AuthServiceLike,
  AuthStateLike,
} from './ports';

/** Texto do botao de entrada. E o unico lugar onde o fornecedor tem nome. */
export const SIGN_IN_LABEL = 'Entrar com a conta Google';

const NO_PROVIDER =
  'A entrada com a sua conta não está configurada nesta instalação. Pode usar o aplicativo na mesma — fica tudo salvo no aparelho.';
const NO_PACKAGE =
  'A entrada com conta não está instalada nesta versão do aplicativo.';

const LOADING: AuthSnapshot = {
  status: 'a-carregar',
  account: null,
  reason: null,
  message: null,
  expiresAt: null,
};

/**
 * Motivo da degradacao, em portugues.
 *
 * A distincao importa para quem usa o aplicativo: sem rede nao ha nada a fazer
 * senao esperar, e oferecer um botao que nao pode funcionar so gera tentativas
 * falhadas; com rede, ha um botao que resolve. E por isso que isto e um campo
 * proprio e nao uma frase — quem decide o que mostrar e a tela, nao este mapa.
 */
const REASON: Record<'expired_offline' | 'refresh_failed', AuthSnapshot['reason']> = {
  expired_offline: 'expirada-sem-rede',
  refresh_failed: 'renovacao-falhou',
};

/** Converte o estado do pacote no que a casca mostra. */
export function toSnapshot(state: AuthStateLike): AuthSnapshot {
  switch (state.status) {
    case 'loading':
      return LOADING;
    case 'signed_out':
      return {
        status: 'desligada',
        account: null,
        reason: null,
        message: state.reason ?? null,
        expiresAt: null,
      };
    case 'active':
      return {
        status: 'ativa',
        account: state.session.user,
        reason: null,
        message: null,
        expiresAt: new Date(state.session.tokens.expiresAt).toISOString(),
      };
    case 'degraded':
      return {
        status: 'degradada',
        account: state.session.user,
        reason: REASON[state.reason],
        message: state.message,
        expiresAt: new Date(state.session.tokens.expiresAt).toISOString(),
      };
  }
}

/** Controlador que so sabe dizer, em portugues, porque nao esta disponivel. */
export function unavailableAuth(reason: string): AuthControllerLike {
  const snapshot: AuthSnapshot = {
    status: 'desligada',
    account: null,
    // Nao ha fornecedor nenhum: isto nao e uma sessao degradada, e a ausencia
    // de sessao. `reason` fala de tokens; aqui nunca houve token.
    reason: null,
    message: reason,
    expiresAt: null,
  };
  return {
    available: false,
    signInLabel: SIGN_IN_LABEL,
    unavailableReason: reason,
    snapshot: () => snapshot,
    async signIn() {
      return snapshot;
    },
    async signOut() {
      /* nada para terminar */
    },
    async useFamily() {
      /* sem fornecedor nao ha nada a informar */
    },
    async getIdToken() {
      return null;
    },
    canSync: () => false,
    subscribe(listener) {
      listener(snapshot);
      return () => {};
    },
    stop() {
      /* nada por parar */
    },
  };
}

export interface CreateAuthOptions {
  core: FlosCore;
  module: AuthModulePort | null;
  /**
   * Identificador publico do cliente OAuth. Nao e segredo (docs/SECURITY.md):
   * o que protege e a origem autorizada, nao o sigilo deste valor.
   */
  clientId?: string | undefined;
  /** Servico ja construido. Usado nos testes, para nao tocar em rede nenhuma. */
  service?: AuthServiceLike;
}

/**
 * Constroi o controlador e restaura a sessao salva.
 *
 * Nunca lança. Um pacote de autenticacao partido e uma linha no diagnostico —
 * o aplicativo abre na mesma e os dados locais continuam la.
 */
export async function startAuth(options: CreateAuthOptions): Promise<AuthControllerLike> {
  const { core } = options;

  let service: AuthServiceLike;
  if (options.service) {
    service = options.service;
  } else {
    if (!options.module) return unavailableAuth(NO_PACKAGE);
    if (!options.clientId) return unavailableAuth(NO_PROVIDER);
    try {
      const provider = new options.module.GoogleAuthProvider({ clientId: options.clientId });
      service = options.module.createAuthService({
        provider,
        events: core.events,
        logger: core.logger,
      });
    } catch (error) {
      core.logger.warn('Nao consegui preparar a entrada com conta', {
        erro: error instanceof Error ? error.message : String(error),
      });
      return unavailableAuth(NO_PROVIDER);
    }
  }

  const listeners = new Set<(snapshot: AuthSnapshot) => void>();
  let current: AuthSnapshot = LOADING;

  const publish = (state: AuthStateLike): AuthSnapshot => {
    current = toSnapshot(state);
    for (const listener of listeners) {
      try {
        listener(current);
      } catch (error) {
        core.logger.error('Ouvinte do estado da conta falhou', {
          erro: error instanceof Error ? error.message : String(error),
        });
      }
    }
    return current;
  };

  const offService = service.subscribe(publish);

  try {
    publish(await service.initialize());
  } catch (error) {
    // `initialize()` promete nao lançar, mas confiar nisso seria uma tela branca
    // a espera de acontecer.
    core.logger.warn('Nao consegui restaurar a conta salva', {
      erro: error instanceof Error ? error.message : String(error),
    });
    publish({ status: 'signed_out' });
  }

  return {
    available: true,
    signInLabel: SIGN_IN_LABEL,
    unavailableReason: null,
    snapshot: () => current,
    async signIn() {
      return publish(await service.signIn());
    },
    async signOut() {
      publish(await service.signOut());
    },
    async useFamily(familyId, memberId) {
      publish(await service.setFamily(familyId, memberId));
    },
    getIdToken: () => service.getIdToken(),
    canSync: () => current.status === 'ativa',
    subscribe(listener) {
      listeners.add(listener);
      listener(current);
      return () => listeners.delete(listener);
    },
    stop() {
      offService();
      listeners.clear();
      service.dispose();
    },
  };
}
